import React, { useState, useEffect, useCallback } from 'react';
import { apiEndpoints, handleApiError } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  Plus, Edit, Trash2, RefreshCw, Search, Package, X
} from 'lucide-react';

const ProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    brand: '',
    model: '',
    unit: 'pieces',
    minimumQuantity: 0,
    location: '',
    notes: ''
  });

  const unitsOptions = ['pieces', 'kg', 'liters', 'boxes', 'bags', 'meters', 'sets'];

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiEndpoints.inventory.getAll({ limit: 500 });
      const rawData = response.data;
      const itemsList = Array.isArray(rawData)
        ? rawData
        : Array.isArray(rawData?.items)
          ? rawData.items
          : [];
      setProducts(itemsList);
    } catch (error) {
      handleApiError(error, 'Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleAdd = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      brand: '',
      model: '',
      unit: 'pieces',
      minimumQuantity: 0,
      location: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      brand: product.brand || '',
      model: product.model || '',
      unit: product.unit || 'pieces',
      minimumQuantity: product.minimumQuantity || 0,
      location: product.location || '',
      notes: product.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      return;
    }

    try {
      await apiEndpoints.inventory.delete(id);
      toast.success('Produit supprimé avec succès');
      fetchProducts();
    } catch (error) {
      handleApiError(error, 'Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.name.trim()) {
      toast.error('Le nom du produit est requis');
      return;
    }

    try {
      const data = {
        name: productForm.name.trim(),
        brand: productForm.brand.trim() || null,
        model: productForm.model.trim() || null,
        unit: productForm.unit || 'pieces',
        minimumQuantity: parseFloat(productForm.minimumQuantity) || 0,
        location: productForm.location.trim() || null,
        notes: productForm.notes.trim() || null,
        category: 'Equipment', // Default required field
        currentQuantity: editingProduct ? editingProduct.currentQuantity : 0
      };

      if (editingProduct) {
        await apiEndpoints.inventory.update(editingProduct.id, data);
        toast.success('Produit modifié avec succès');
      } else {
        await apiEndpoints.inventory.create(data);
        toast.success('Produit créé avec succès');
      }
      setShowModal(false);
      fetchProducts();
    } catch (error) {
      handleApiError(error, 'Erreur lors de l\'enregistrement');
    }
  };

  const filteredProducts = products.filter(p =>
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.location || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-7 w-7 text-indigo-600" />
            Gestion des Produits (Catalogue)
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Gérez votre catalogue de produits (Créer, Modifier, Supprimer)
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 font-medium text-sm flex items-center gap-2 shadow-sm transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nouveau Produit
        </button>
      </div>

      {/* Search & Actions */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher par nom, marque, modèle, emplacement..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
          />
        </div>
        <button
          onClick={fetchProducts}
          className="px-3.5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm font-medium transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-400 gap-3">
            <RefreshCw className="h-7 w-7 animate-spin text-indigo-600" />
            <span className="text-sm">Chargement des produits...</span>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Package className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-base">Aucun produit dans le catalogue</p>
            <p className="text-sm text-gray-400 mt-1">
              {searchTerm ? 'Essayez une autre recherche' : 'Cliquez sur "Nouveau Produit" pour commencer'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Nom du Produit
                  </th>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Marque / Modèle
                  </th>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Unité
                  </th>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Stock d'alerte min.
                  </th>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Emplacement
                  </th>
                  <th className="px-6 py-3.5 text-right font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="px-6 py-4 font-semibold text-gray-900 whitespace-nowrap">
                      {product.name}
                    </td>
                    <td className="px-6 py-4 text-gray-600 whitespace-nowrap">
                      {[product.brand, product.model].filter(Boolean).join(' - ') || '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-600 whitespace-nowrap capitalize">
                      {product.unit}
                    </td>
                    <td className="px-6 py-4 text-gray-600 whitespace-nowrap">
                      {product.minimumQuantity} {product.unit}
                    </td>
                    <td className="px-6 py-4 text-gray-600 whitespace-nowrap">
                      {product.location || '-'}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="p-1.5 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-md transition-colors"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                {editingProduct ? 'Modifier le Produit' : 'Ajouter un Produit'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                  Nom du Produit *
                </label>
                <input
                  type="text"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  placeholder="Ex: Selle d'équitation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Marque
                  </label>
                  <input
                    type="text"
                    value={productForm.brand}
                    onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })}
                    placeholder="Ex: Fouganza"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Modèle
                  </label>
                  <input
                    type="text"
                    value={productForm.model}
                    onChange={(e) => setProductForm({ ...productForm, model: e.target.value })}
                    placeholder="Ex: PRO 500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Unité *
                  </label>
                  <select
                    value={productForm.unit}
                    onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    {unitsOptions.map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Stock d'alerte min.
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={productForm.minimumQuantity}
                    onChange={(e) => setProductForm({ ...productForm, minimumQuantity: e.target.value })}
                    placeholder="Ex: 5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                  Emplacement / Dépôt
                </label>
                <input
                  type="text"
                  value={productForm.location}
                  onChange={(e) => setProductForm({ ...productForm, location: e.target.value })}
                  placeholder="Ex: Dépôt A - Etagère 3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  rows={2}
                  value={productForm.notes}
                  onChange={(e) => setProductForm({ ...productForm, notes: e.target.value })}
                  placeholder="Notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                >
                  {editingProduct ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsManagement;
